// Types to pass to the recommender.
// We use camel case here since we are going to send this
import { type Static, Type } from "@sinclair/typebox";
import type {
  IntervalUnit,
  RootCondition,
  RelatedColumn,
  ColumnType,
  PropertyCondition,
  AndCondition,
  OrCondition,
  EventFilter,
} from "../../query/visual/types";
import type { RawSchemaName } from "../../sync/planner/in-warehouse/adapter-types";
import {
  FM_COLUMN_TYPE_UNIX_TIMESTAMP,
  type FMColumnType,
} from "./column-types";
import { InputFeatureSchema } from "./input-feature-schema";
import type { ConsentPurgeMode } from "./consent-purge";
import type { OverridePeriod } from "./override-periods/types";
import type * as types from "zapatos/schema";
import type { FlowSendInsightViews } from "./send-insights/types";
import type {
  FilterableColumn,
  MessagePreviewSnippetCheck,
  MessagePreviewTestUserCheck,
} from "@hightouch/core/server/graphql/types";

export interface DecisionEngineRow {
  id: string;
  workspaceId: string;
  config: DecisionEngineConfig;
  segmentId: number;
}

export interface DecisionEngineFlowRow {
  id: string;
  name: string;
  config: DecisionEngineFlowConfig;
  audienceId: number;
  parentModelId: number;
  timing: RecommenderTiming;
  /** ID of the linked feature matrix for auto-generating user feature SQL */
  decision_engine_feature_matrix_id?: string | null;
  flow_type: DecisionEngineFlowType;
}

export interface DecisionEngineModelRow {
  id: string;
  name: string;
  config: DecisionEngineModelConfig;
}

export interface DecisionEngineSplitConfig {
  id?: string;
  name: string;
  variants: DecisionEngineSplitVariant[];
}

export interface DecisionEngineSplitVariant {
  name: string;
  audience_percent: number;
  models: ModelMapping;
}

export interface ModelToTrain extends DecisionEngineModelRow {
  splitName?: string;
  splitVariant?: string;
}

// over the network to the recommender.
export interface DecisionEngineChannel {
  name: string;
  messages: DecisionEngineMessage[];
  tags: string[];
  quiet_period_days?: Day[];
  quiet_period_times?: Time[];
  disabled_messages?: DecisionEngineMessage[];
  max_slots?: number;
}

export enum DecisionEngineStatus {
  PENDING = "pending",
  TRAINING = "training",
  READY = "ready",
}

export enum DecisionEngineSplitStatus {
  Draft = "draft",
  Scheduled = "scheduled",
  Active = "active",
  Ended = "ended",
}

export enum SuggestedVariantStatus {
  DRAFT = "draft",
  ACCEPTED = "accepted",
  REJECTED = "rejected",
}

export enum OutcomeRewardMode {
  STATIC = "static",
  COLUMN_VALUE = "column_value",
  COLUMN_MULTIPLIED = "column_multiplied",
}

export enum OfferConfigType {
  STATIC = "static",
  COLUMN = "column",
}

export interface StaticOfferConfig {
  type: OfferConfigType.STATIC;
  cost_value: number;
  cost_multiplier: number;
  cost_cap?: number | null;
}

export interface ColumnOfferConfig {
  type: OfferConfigType.COLUMN;
  value_column: string;
  cost_column: string;
  cost_value: number;
  cost_multiplier: number;
  cost_cap?: number | null;
}

export type OfferCostConfig = StaticOfferConfig | ColumnOfferConfig;

export interface OfferConfig {
  id: string;
  name: string;
  description: string | null;
  external_id: string | null;
  config: OfferCostConfig;
  tags: string[];
}

export function isColumnOfferConfig(
  config: OfferCostConfig,
): config is ColumnOfferConfig {
  return config.type === OfferConfigType.COLUMN;
}

/**
 * Collect the distinct parent-model columns referenced by column-based offers
 *
 * These are consumed two ways that must stay in sync: they're declared in the recommender feature schema as `dependency`
 * columns and they must be pulled from the parent model as bare pass-through columns. Column names are already lowercased
 * at save time, matching the lowercased feature schema.
 */
export function getColumnOfferParentColumns(
  channels: DecisionEngineChannel[],
): string[] {
  const columns = new Set<string>();
  for (const channel of channels) {
    for (const message of channel.messages) {
      if (message.offer && isColumnOfferConfig(message.offer.config)) {
        columns.add(message.offer.config.cost_column);
        columns.add(message.offer.config.value_column);
      }
      for (const variable of message.variables) {
        for (const variant of variable.variants) {
          if (variant.offer && isColumnOfferConfig(variant.offer.config)) {
            columns.add(variant.offer.config.cost_column);
            columns.add(variant.offer.config.value_column);
          }
        }
      }
    }
  }
  return [...columns];
}

export enum OutcomeRewardTransform {
  NO_TRANSFORM = "no_transform",
  LOG1P = "log1p",
  SQRT = "sqrt",
  SQUARE = "square",
}

/** Channels a generated sync delivers through. Shared by AID channels and journey sync nodes. */
export const DELIVERY_CHANNEL_TYPES = [
  "email",
  "push",
  "sms",
  "inapp",
  "surface",
] as const;
export type DeliveryChannelType = (typeof DELIVERY_CHANNEL_TYPES)[number];

export type DecisionEngineChannelType =
  | DeliveryChannelType
  | "raw"
  // A journey decision node's channel. It parents the node's per-option
  // messages and never sends, so it has no destination.
  | "journey";

/** Never delivers, so code expecting a campaign id or destination must skip it. */
export const JOURNEY_CHANNEL_TYPE = "journey" as const;

export const RAW_CHANNEL_TYPE = "raw" as const;

/** Channel types that may be saved without a destination, because Hightouch does not create or manage their syncs. */
export const DESTINATION_OPTIONAL_CHANNEL_TYPES = [
  JOURNEY_CHANNEL_TYPE,
  RAW_CHANNEL_TYPE,
] as const satisfies readonly DecisionEngineChannelType[];

export function isDestinationOptionalChannelType(type: string): boolean {
  return (DESTINATION_OPTIONAL_CHANNEL_TYPES as readonly string[]).includes(
    type,
  );
}

/** A flow whose decisions are made by a journey rather than by a send. */
export const JOURNEY_FLOW_TYPE = "journey" as const;

/** status is free text in the column and in the create-flow input. */
export function isDecisionEngineStatus(
  value: unknown,
): value is DecisionEngineStatus {
  return Object.values(DecisionEngineStatus).includes(
    value as DecisionEngineStatus,
  );
}

export function asDecisionEngineStatus(value: unknown): DecisionEngineStatus {
  if (!isDecisionEngineStatus(value)) {
    // Plain Error, not VError: 157 app files import this module, and a runtime
    // import of the backend SDK here pulls it into the frontend's program.
    throw new Error(
      `Unknown decision engine status "${String(value)}"; expected one of ${Object.values(DecisionEngineStatus).join(", ")}.`,
    );
  }
  return value;
}

export const DECISION_ENGINE_FLOW_TYPES = [
  "messaging",
  "onsite",
  JOURNEY_FLOW_TYPE,
] as const;

export type DecisionEngineFlowType =
  (typeof DECISION_ENGINE_FLOW_TYPES)[number];

/** flow_type is free text in the column and in the create-flow input. */
export function isDecisionEngineFlowType(
  value: unknown,
): value is DecisionEngineFlowType {
  return DECISION_ENGINE_FLOW_TYPES.includes(value as DecisionEngineFlowType);
}

export function asDecisionEngineFlowType(
  value: unknown,
): DecisionEngineFlowType {
  if (!isDecisionEngineFlowType(value)) {
    // Plain Error, not VError: 157 app files import this module, and a runtime
    // import of the backend SDK here pulls it into the frontend's program.
    throw new Error(
      `Unknown decision engine flow type "${String(value)}"; expected one of ${DECISION_ENGINE_FLOW_TYPES.join(", ")}.`,
    );
  }
  return value;
}

/** A flow row as stored, with flow_type narrowed from the column's text. */
export type DecisionEngineFlowSelectable = Omit<
  types.decision_engine_flows.JSONSelectable,
  "flow_type"
> & { flow_type: DecisionEngineFlowType };

/** The journey node an arm message stands for. */
export type JourneyArmMessageConfig = {
  journeyNodeId: string;
};

/**
 * How message previews address the ESP recipient for a channel.
 *
 * - "synthetic" (default): one per-message inbox address
 *   (`{ws}_{msg}_s0@…`); the ESP creates one synthetic record per message.
 * - "registered": one per-account address (`{ws}_account_s0@…`) that the
 *   customer registers/allowlists in the ESP once; correlation to a message
 *   comes from the AidPreviewTraceId echoed in the rendered HTML. Preview
 *   sends must never create ESP records in this mode.
 */
export type DecisionEnginePreviewRecipientMode = "synthetic" | "registered";

export type DecisionEngineChannelConfigType =
  | "dataExtensions"
  | "trigger"
  | "canvas"
  | "journeyEntryEvent"
  | "customDataObject"
  | "template"
  | "campaign";

export enum DecisionEngineFlowCommitStatus {
  committing = "committing",
  committed = "committed",
}

interface BaseDecisionEngineFlowCommitData {
  commitSource: string;
  jobId?: string;
  overwriteConflictingDays?: boolean;
  invalidateManifest?: boolean;
  mergedInteractionsPaths?: string[];
}

export interface DecisionEngineFlowCommitData
  extends BaseDecisionEngineFlowCommitData {
  manifestPath: string;
  /** S3 paths to copy new interactions from. */
  newInteractionsPaths?: string[];
  /**
   * @deprecated Use newInteractionsPaths. Kept for backward compatibility
   * with in-flight commits stored in DB.
   * TODO: Remove - https://linear.app/hightouchio/issue/DE-2998
   */
  newInteractionsPath?: string;
  /* Directories on disk to copy policy data and intelligent targeting data from. */
  policyDataDirectory: string;
  intelligentTargetingDataDirectory: string;
}

/**
 * Storage-based variant of DecisionEngineFlowCommitData.
 * All paths are storage keys or prefixes (not local filesystem paths).
 */
export interface DecisionEngineFlowCommitDataFromStorage
  extends BaseDecisionEngineFlowCommitData {
  manifestKey: string;
  newInteractionsPaths?: string[];
  policyDataPrefixes: string[];
  intelligentTargetingDataPrefixes: string[];
}

/**
 * Destination-specific settings a generated message sync is built from.
 * Everything a destination definition's `generateSyncConfig` may read lives
 * here, so any producer (an AID channel, a journey sync node) can supply it.
 */
export interface DestinationSyncSettings {
  // Shared by every destination.

  // Selects the definition variant for destinations with more than one
  // (e.g. "sfmc:trigger" vs "sfmc:dataExtensions").
  type?: DecisionEngineChannelConfigType;
  // *AJO recipient can be a custom type
  recipientType?: "email" | "userId" | "phone" | string;
  // Columns packed into `hightouch_user`. Marketo and SFMC also map each one to its own destination field.
  userColumns?: UserColumn[];
  // Registered-preview sends, profile-keyed ESPs only: the destination-side
  // identifier of the one test profile whose email attribute is the account
  // preview address (e.g. Iterable user ID, Marketo lead ID). Read by the
  // generator only when the spec asks for a registered preview.
  previewRecipientKey?: string;

  // Adobe Experience Platform and Adobe Campaign Classic.
  channelDataType?: "emailAddress" | "mobilePhoneNumber";
  channelDataColumn?: string; // Column that will point to the channel data field (i.e. email or phone)
  customResourceName?: string; // ACC custom resource table name

  // Eloqua.
  sendFrom?: string; // User ID of the sender if don't want to use the default user on the destination

  // SFMC.
  businessUnit?: string;
  sendClassificationId?: string;
  // Data extension sync mode, e.g. "mirror" or "upsert".
  syncMode?: string;
  // XXX: use if want to keep channel in the legacy sfmc DE flow
  useLegacyDataExtension?: boolean;

  // MongoDB.
  collectionName?: string;
  mongoIdField?: string;

  // Personalization API and MongoDB.
  // Onsite placement config (when used as a placement in an onsite flow)
  placement?: OnsitePlacementConfig;
}

/**
 * How far back a delivery reads due recommendations. Bounds the burst when a
 * message or sync is re-enabled after a gap.
 */
export const DEFAULT_SYNC_LOOKBACK_INTERVAL_HOURS = 2;

/**
 * An AID channel's config: the destination sync settings plus the fields only
 * the AID channel and its model query use.
 */
export interface DecisionEngineChannelConfig extends DestinationSyncSettings {
  externalId: string;

  useChannelLevelSync?: boolean;

  // Message-preview recipient addressing for this channel; absent means
  // "synthetic". The preview resolver passes it to the generator as a param.
  previewRecipientMode?: DecisionEnginePreviewRecipientMode;
  previewTestUserCheck?: Omit<MessagePreviewTestUserCheck, "__typename">;
  previewSyntheticRejectedAt?: string;
  previewSyntheticRejectCode?: "recipient_rejected";
  previewRecipientRevision?: number;

  // Generic sync config override
  syncConfigOverride?: Record<string, unknown>;

  // Appends array values to matching array properties in the generated sync config
  syncConfigAppend?: Record<string, unknown[]>;

  quietPeriodConfig?: {
    days: Day[];
    times: Time[];
    enabled: boolean;
  };

  userAudienceFilter?: RootCondition;

  // The maximum number of hours before now to look back for sends in the channel sync query.
  // Prevents a flood of sends if a sync is re-enabled. Defaults to DEFAULT_SYNC_LOOKBACK_INTERVAL_HOURS.
  syncLookbackIntervalHours?: number;

  // Cron expression override for syncs on this channel (e.g., "10 * * * *").
  // Takes priority over destination-specific and default schedules.
  syncScheduleOverride?: string;

  // Custom tags for this channel (key:value pairs, e.g., { use_case: "promotional" })
  tags?: Record<string, string>;

  // Overrides in joining back to the audience
  // Needed originally for Accor since they use a tokenized version of customer emails
  // but we need the cleartext email for syncing
  audienceJoinColumnOverride?: string;
}

export interface OnsitePlacementConfig {
  placement_id: string;
  placement_type: "single" | "multi";
  max_slots?: number;
  content_schema: ContentSchemaField[];
  preview_html?: string;
}

export interface ContentSchemaField {
  name: string;
  type: "string" | "number" | "boolean";
  required?: boolean;
}

export interface UserColumn {
  column: string;
  type: ColumnType;
  overridePropertyName?: string;
}

export interface DecisionEngineMessage {
  id: string;
  name: string;
  campaign_id: string;
  variables: Variable[];
  content?: string;
  items: DecisionEngineItem[];
  max_sends_per_user?: number;
  max_sends_per_user_window_days?: number;
  min_other_messages_in_between?: number;
  first_eligible_send_date?: string;
  last_eligible_send_date?: string;
  tags?: string[];
  embedding?: number[];
  embedding_with_variables?: number[];
  can_send_column?: string;
  has_guardrail?: boolean;
  offer?: OfferConfig;
  eligible_schedule_variants?: ScheduleVariant[];
  disabled_at?: string;
  paused_periods?: TimeWindow[];
  active_only_periods?: TimeWindow[];
}

export interface VariableVariantDB {
  value: string;
  tags?: Record<string, string>;
  firstEligibleSendDate?: string;
  lastEligibleSendDate?: string;
  userAudienceFilter?: RootCondition;
  embedding?: Array<number>;
  localeValues?: Record<string, string>;
  displayOrder?: number;
  offerId?: string;
}

export const FOREVER_SEND_LIMIT_MAX_SENDS = 1;

type SendLimit = Pick<
  VariableDB,
  "maxSendsPerUser" | "maxSendsPerUserWindowDays"
>;

export function isForeverSendLimit({
  maxSendsPerUser,
  maxSendsPerUserWindowDays,
}: SendLimit): boolean {
  return maxSendsPerUser != null && maxSendsPerUserWindowDays == null;
}

export function foreverSendLimitError(
  limit: SendLimit,
  subject: string,
): string | null {
  const { maxSendsPerUser } = limit;
  if (
    isForeverSendLimit(limit) &&
    maxSendsPerUser != null &&
    maxSendsPerUser > FOREVER_SEND_LIMIT_MAX_SENDS
  ) {
    return `${subject} has a send limit of ${maxSendsPerUser} with no time window. A send limit with no time window allows at most ${FOREVER_SEND_LIMIT_MAX_SENDS} send.`;
  }
  return null;
}

export function assertValidForeverSendLimit(
  limit: SendLimit,
  subject: string,
): void {
  const error = foreverSendLimitError(limit, subject);
  if (error) {
    throw new Error(error);
  }
}

/** The Native Delivery dynamic content group a variable stands for; its values are the group's branch ids. */
export type VariableSource = { type: "dynamic_slot"; libraryId: string };

export interface VariableConfigDB {
  maxSendsPerUser?: number;
  maxSendsPerUserWindowDays?: number;
  localizationEnabled?: boolean;
  source?: VariableSource;
}

// We need this type and the below Variable so that we can handle the fact that tags are stored as k:v in the db
export interface VariableDB {
  name: string;
  variants: VariableVariantDB[];
  maxSendsPerUser?: number;
  maxSendsPerUserWindowDays?: number;
  displayOrder?: number;
  /** Whether localization is enabled for this variable (defaults to false) */
  localizationEnabled?: boolean;
  /** Set when Native Delivery registered the variable; the content tab then keeps its name and values read-only. */
  source?: VariableSource;
}

export function extractVariableValues(variable: VariableDB): string[] {
  return variable.variants.map((variant) => variant.value);
}

export interface VariableVariant {
  id: string;
  value: string;
  tags?: string[];
  first_eligible_send_date?: string;
  last_eligible_send_date?: string;
  max_sends_per_user?: number;
  max_sends_per_user_window_days?: number;
  /** Localized values for this variant, keyed by locale code (e.g., "en-US", "fr-FR") */
  locale_values?: Record<string, string>;
  can_send_column?: string;
  has_guardrail?: boolean;
  offer?: OfferConfig;
}

export interface VariableVariantConfigDB {
  firstEligibleSendDate?: string;
  lastEligibleSendDate?: string;
}

export interface VariableVariantLocaleValuesDB {
  [locale: string]: string;
}

// Used for variables in DecisionEngineMessage after transformation
export interface Variable {
  name: string;
  variants: VariableVariant[];
  localization_enabled: boolean;
}

export enum Day {
  Sunday = "sunday",
  Monday = "monday",
  Tuesday = "tuesday",
  Wednesday = "wednesday",
  Thursday = "thursday",
  Friday = "friday",
  Saturday = "saturday",
}

export enum Time {
  Midnight = "midnight",
  LateNight = "late_night",
  EarlyMorning = "early_morning",
  Morning = "morning",
  Afternoon = "afternoon",
  MidAfternoon = "mid_afternoon",
  Evening = "evening",
  Night = "night",
}

export type UserFrequencyLimit = {
  max_sends: number;
  in_last_days: number;
};

export const ONSITE_PERMISSIVE_FREQUENCY_LIMIT = {
  max_sends: 1_000_000,
  in_last_days: 7,
} satisfies UserFrequencyLimit;

export interface TimeWindow {
  start: Date;
  end: Date;
}

export type DecisionRefreshIntervalUnit = "hour" | "day";

export interface DecisionRefreshInterval {
  value: number;
  unit: DecisionRefreshIntervalUnit;
}

export interface DecisionRefreshConfig {
  interval: DecisionRefreshInterval;
  force_update_after_impression?: boolean;
}

export interface BaseRecommenderTiming {
  frequency_limit: UserFrequencyLimit;
  min_hours_between_sends?: number; // derived, not stored in db. Passed to DE
  max_sends_per_slot_override?: number;
  decision_refresh_config?: DecisionRefreshConfig;
}

export type SchedulingMode = "optimized" | "fixed" | "on_demand";

export type TimeBucketOffset = 0 | 1 | 2;
export type TimeBucketOffsets = Partial<Record<Time, TimeBucketOffset>>;

export interface RecommenderTiming extends BaseRecommenderTiming {
  time_bucket_offsets?: TimeBucketOffsets;
  days: Day[];
  times: Time[];
  send_time_buffer_hours_override?: number;
  eligible_days_override?: number;
  blackout_days?: string[];
  modeling_blackout_windows?: TimeWindow[];
  scheduling_mode?: SchedulingMode;
  rollout_fill_best_set?: boolean;
}

export interface DecisionEngineItem {
  collection_id: string;
  num_items_to_recommend: number;
}

export const DEFAULT_SCHEDULING_TIMEZONE = "America/Los_Angeles";

// Default attribution window in days
export const DEFAULT_ATTRIBUTION_WINDOW_DAYS = 3;

// Default Slack channel for notifications
export const DEFAULT_SLACK_CHANNEL = "C07RGLSVATU";

// Default days to complete message
export const DEFAULT_MESSAGE_COMPLETION_INTERVAL_DAYS = 3;

export type TagsSchema = Record<
  string,
  {
    tag_values: {
      value: string;
      description?: string; // Description for the tag value for llm generation
    }[];
    description?: string; // Description for the tag key for llm generation
  }
>;

export type TimezoneFormat = "iana" | "offset_string";

export function isTimezoneFormat(value: unknown): value is TimezoneFormat {
  return value === "iana" || value === "offset_string";
}

export type OrchestratorTimezoneColumnConfig = {
  format: TimezoneFormat;
} & ({ name: string } | { column: RelatedColumn });

export interface OutcomeTimezoneConfig {
  format: TimezoneFormat;
  value: string;
}

export type OrchestratorExperimentGroupColumnConfig =
  | string
  | { column: RelatedColumn };

// Internal types used by the decision engine orchestrator to prepare inputs
// and process outputs.
export interface DecisionEngineConfig {
  slack_channel?: string;
  // The rest of this is set via the admin resolvers
  name: string;
  // Optional override for the measurement window days for insights queries.
  measurement_window_days?: number;
  // Optional override for the output schema (if we don't want hightouch_planner)
  output_schema?: RawSchemaName;
  // DE-level setting (staff-configured via the admin config resolver). Not
  // per-flow — readers source this directly from the DE config.
  message_completion_interval_days?: number;
  // Always run decision engine regardless of COMMIT or ROLLBACK
  ignore_s3_state?: boolean;
  // DE-level setting. Not per-flow — readers source this directly from the DE config.
  history_dimensions?: string[];

  // We assume customers will set a lifecycle rule on their bucket of 30 days or longer
  // However, if they set a shorter rule, cached interactions will expire unless touched more frequently
  // If data expires after x days, set this to less than x to ensure data does not expire
  touch_cached_interactions_older_than_days?: number;
  company_blurb?: string;

  // Tags schema for messages, variants, and channels (key: tag key, value: TagKeySchema)
  message_tags_schema?: TagsSchema;
  variant_tags_schema?: TagsSchema;
  channel_tags_schema?: TagsSchema;

  // Clear existing attribution state on run
  clear_attribution_state?: boolean;

  // Maximum number of user features that can be selected for insights
  max_user_features_for_insights?: number;
}

export interface TrainingWindow {
  start_days_ago?: number;
  end_days_ago?: number;
  train_eligible_start_date?: string; // (e.g., "2024-01-01")
  train_eligible_end_date?: string; // (e.g., "2024-12-31")
}

export interface DownsampleConfig {
  zero_reward_downsampling_enabled?: boolean;
  max_rows_with_zero_reward?: number;
  conversion_rate?: number;
  auto_adjust_zero_reward_downsampling?: boolean;
  max_ipw_weight?: number;
  temporal_enabled?: boolean;
  temporal_decay_rate?: number;
  training_matrix_max_rows?: number;
}

export interface UpliftModelConfiguration {
  uplift_model_type?: "s_learner" | "dr_learner";
  uplift_modeling_enabled?: boolean;
}

/**
 * Magnitude settings for a composite reward model. Mirrors the Python
 * `MagnitudeConfig`: a missing `magnitude` key means Python fills the defaults
 * in whenever the composite is enabled. Fields are intentionally non-nullable
 * because the Python side forbids nulls. `min_occurrences` is the minimum
 * number of occurrence rows (rows with a non-zero outcome value) required to
 * train the magnitude regressor.
 */
export interface MagnitudeConfig {
  objective?: "regression" | "tweedie" | "gamma";
  min_occurrences?: number;
}

export interface ModelConfiguration {
  outcome_reward_function_id?: string;
  /** Prior promotion state. Node overwrites it every run from the last production record. */
  predictive_model_promoted?: boolean;
  /**
   * Admin override. Stage 2 model quality guards are recorded but never block
   * the verdict. Stage 1 data guards still apply. Never promotes.
   */
  skip_quality_guards?: boolean;
  magnitude?: MagnitudeConfig;
  uplift_model_configuration?: UpliftModelConfiguration;
  primary_scoring_metric?: ScoringMetric;
  n_jobs?: number;
  /**
   * Per-flow cap on concurrent estimator fits inside one fold worker.
   * Absent or null resolves the width from the pod, 1 forces sequential fits.
   */
  max_concurrent_estimator_fits?: number | null;
  pre_dispatch?: number | string;
  downsampling?: DownsampleConfig;
  skip_background_models?: boolean;
  sklearn_verbosity?: number;
  training_window?: TrainingWindow;
  enable_stagewise_imputation?: boolean;
  fixed_model_params?: Record<string, any>;
  training_matrix_filter?: RootCondition | DecisionEngineCondition | null;
}

export interface OutcomeConfigBase {
  id: string;
  name: string;
  campaign_id_column?: string;
}

export interface OutcomeConfig extends OutcomeConfigBase {
  reward_value?: number;
  use_dynamic_weights?: boolean;
  reward_mode?: OutcomeRewardMode;
  reward_transform?: OutcomeRewardTransform;
  measurement_only?: boolean;
}

export interface OptunaConfig {
  enabled?: boolean;
  model_name?: string;
  n_trials?: number;
  search_space?: object[];
  run_interval_days?: number;
  force_run?: boolean;
  force_trial_params?: object[];
  n_jobs?: number;
  /**
   * Escape hatch for the fold-transform cache. False refits each fold's
   * preprocessing every trial.
   */
  reuse_fold_transforms?: boolean;
}

export const DEFAULT_OPTUNA_RUN_INTERVAL_DAYS = 7;

// Base interface for common chart properties
interface BaseChartDefinition {
  id: string;
  breakdown_by_type: "user_feature" | "action_feature";
  breakdown_by_dimension: string;
  rank: number;
  metric_config: {
    buckets?: BucketRange[];
    metric: "per_interaction_conversion_rate";
  };
  conversion_data: ConversionData[];
  filter_by?: string;
  filter_by_type?: "user_feature" | "action_feature";
  filter_value?: string | BucketRange | boolean;
  statistics?: ChartStatistics;
}

export type GlobalBreakdownChartDefinition = BaseChartDefinition & {
  chart_type: "global";
};

export type CorrelationBreakdownChartDefinition = BaseChartDefinition & {
  chart_type: "correlation";
};

export type HeatmapChartDefinition = Omit<
  BaseChartDefinition,
  "conversion_data" | "filter_value"
> & {
  chart_type: "heatmap";
  filter_by: string;
  filter_by_type: "user_feature" | "action_feature";
  filter_value: null;
  heatmap_cells: HeatmapCell[];
};

export type ChartDefinition =
  | GlobalBreakdownChartDefinition
  | CorrelationBreakdownChartDefinition
  | HeatmapChartDefinition;

export type AugmentedChartDefinition = Omit<
  ChartDefinition,
  "conversion_data" | "heatmap_cells" | "statistics"
> & {
  top_chart: boolean;
  metadata: {
    conversion_data?: ConversionData[];
    heatmap_cells?: HeatmapCell[];
    chart_description?: string;
    top_chart_reason?: string;
    statistics?: ChartStatistics;
  };
};

export type BucketRange = {
  min: number;
  max: number;
  quartile: number;
};

// chart level summary statistics
export type ChartStatistics = {
  chart_score?: number;
  total_coverage?: number;
  baseline_rate?: number;
  filter_baseline_rate?: number;
  n_groups_total?: number;
  n_groups_credibly_different?: number;
  credibly_different?: boolean;
  main_effect?: number | null;
};

export type EffectDirection = "higher" | "lower";

export type ConversionData = {
  breakdown_value: string;
  n_total_conversions: number;
  n_total_interactions: number;
  conversion_rate: number;
  baseline_rate: number;
  comparison_rate?: number;
  effect_size_abs?: number;
  rank_score: number;
  credibly_different: boolean;
  direction: EffectDirection | null;
  coverage: number;
};

export type HeatmapCell = {
  row_value: string;
  column_value: string;
  conversion_rate: number;
  expected_conversion_rate: number;
  user_cohort_baseline: number;
  creative_baseline: number;
  global_baseline: number;
  n_total_conversions: number;
  n_total_interactions: number;
  comparison_rate?: number;
  effect_size_abs?: number;
  rank_score: number;
  credibly_different: boolean;
  direction: EffectDirection | null;
  coverage: number;
};

// Flow configs snapshot is used to store the flow configs in a snapshot so that later phases can use the same data that is matched with user features file used in training
export interface FlowConfigSnapshot {
  flowMessages: types.decision_engine_flow_messages.JSONSelectable[];
  messages: types.decision_engine_messages.JSONSelectable[];
  channels: types.decision_engine_channels.JSONSelectable[];
  userFeatures: FeatureSchema;
  messageVariables: Record<string, VariableDB[]>;
  /**
   * The flow timing with the active override period and dry-run options applied.
   * Absent on snapshots written before override periods.
   */
  timing?: RecommenderTiming;
  /**
   * The stored flow timing, before any override period or dry-run option.
   * Absent on older snapshots, which restore with the live base timing.
   */
  baseTiming?: RecommenderTiming;
  overridePeriods: OverridePeriod[];
  activeOverridePeriod: OverridePeriod | null;
}

/** Revives period dates. Snapshots written before override periods read as having no periods. */
export function parseFlowConfigSnapshot(json: string): FlowConfigSnapshot {
  const snapshot: FlowConfigSnapshot = JSON.parse(json);
  const revive = (period: OverridePeriod): OverridePeriod => ({
    ...period,
    startsAt: new Date(period.startsAt),
    endsAt: new Date(period.endsAt),
  });
  return {
    ...snapshot,
    overridePeriods: (snapshot.overridePeriods ?? []).map(revive),
    activeOverridePeriod: snapshot.activeOverridePeriod
      ? revive(snapshot.activeOverridePeriod)
      : null,
  };
}

export function isOnsiteFlow(flowType: string): boolean {
  return flowType === "onsite";
}

/**
 * Whether analytics/insights surfaces should drop interactions with no
 * matching send. Onsite-only: only onsite flows emit a send (impression)
 * event for every experiment group. Training uses the raw config value.
 */
export function shouldFilterUnsentInteractions(flow: {
  config: Pick<DecisionEngineFlowConfig, "filter_unsent_interactions">;
  flow_type: string;
}): boolean {
  return (
    (flow.config.filter_unsent_interactions ?? false) &&
    isOnsiteFlow(flow.flow_type)
  );
}

export interface DecisionEngineFlowRunSchedule {
  cronExpression: string;
}

// Mirrors AttributionType in decision_engine/models.py. The orchestrator always
// sends an explicit value, filling DEFAULT_ATTRIBUTION_TYPE when the flow omits it.
export const ATTRIBUTION_TYPES = ["multi_touch", "last_touch"] as const;
export type AttributionType = (typeof ATTRIBUTION_TYPES)[number];
export const DEFAULT_ATTRIBUTION_TYPE: AttributionType = "last_touch";

export interface DecisionEngineFlowConfig {
  holdout: number;
  model_mapping: ModelMapping;
  // Optional start date for the flow. Allows for configuring one and then having it launch in the future
  flow_start_date?: string;
  // Optional start date for incrementality experiment measurements. Set this if experiment splits have been updated and we want to restart the experiment measurement
  incrementality_experiment_start_date?: string;
  bandits?: BanditConfig;
  breakdown_config?: BreakdownGenerationConfigInternal;
  num_generate_interactions_subprocesses?: number;
  max_df_size_gb?: number;
  users_per_container?: number;
  max_users_per_subprocess?: number;
  pull_concurrency?: number;
  pull_chunk_size?: number;
  include_sends_in_warehouse_pull?: boolean;
  customer_managed_campaign_ids?: string[];
  assign_experiment_groups_in_warehouse?: boolean;
  user_experiment_group_column?: OrchestratorExperimentGroupColumnConfig;
  experiment_group_salt?: string;
  attribution: {
    window: {
      unit: IntervalUnit;
      value: number;
    };
    attribution_type?: AttributionType;
    decay_strategy?: string;
    half_life_days?: number;
  };
  intelligent_targeting?: IntelligentTargetingConfig;
  show_uniform?: boolean;
  uniform_predictive_modeling?: boolean;
  allow_multishot?: boolean;
  enable_exhaustive_schedule_selection?: boolean;
  value_optimization?: ValueOptimizationConfig;
  filter_unsent_interactions?: boolean;
  recreate_cached_interactions_time_limit_hours?: number;
  recreate_cached_interactions_limit_days?: number;
  recreate_cached_interactions_older_than?: string;
  warehouse_pull_page_size?: number;
  agent_goal?: string;
  demo_data?: DemoData;
  interactions_parquet_file_num_rows?: number;
  compact_interactions_time_limit_hours?: number;
  // How many days of completed interactions to pull from warehouse
  interactions_historical_days_to_pull?: number;
  retention_window_days?: number;
  // Set the user features we should use to generate charts for insights
  user_features_for_insights?: string[];
  // Execution window (time of day) - array of eligible hours (0-23)
  eligible_time_window_utc?: number[];
  // Localization settings
  /** Whether localization is enabled for this agent */
  localization_enabled?: boolean;
  /** List of supported locale codes (e.g., ["en-US", "fr-FR"]) */
  locales?: string[];
  /** Default locale to use when user's locale is not found in locale_values */
  default_locale?: string;
  /** Field name in user_features to read locale from (defaults to "locale") */
  user_locale_field?: string;
  /**
   * What to do when a localized variant doesn't have the user's locale:
   * - "default_locale": Use the default locale's translation
   * - "skip": Variant becomes ineligible (don't send)
   * - "throw": Raise an error
   */
  locale_fallback_behavior?: "default_locale" | "skip" | "throw";
  /** Raw user feature name to facet analytics charts on (e.g., 'region'). Not prefixed with 'user_feature_'. */
  analytics_facet_feature?: string;
  /** Raw column name on the parent model to facet analytics by (e.g., 'business_unit'). Preferred over analytics_facet_feature because raw column groupBys are faster. */
  analytics_facet_column?: string;
  /** Hide the customer-managed group from the Performance overview table. Display-only; the group is still assigned, measured, and stored. */
  hide_customer_managed_group?: boolean;
  send_insight_views?: FlowSendInsightViews;
  max_bytes_per_merge_gb?: number;
  warehouse_write_chunk_size_mb?: number;
  max_chunks_per_copy_into?: number;
  /** Tag keys for which to create binary eligibility features (e.g., ["sku_category", "sub_audience"]).**/
  eligibility_tag_keys?: string[];
  /** Skip the experiment group column validation query. */
  skip_experiment_group_validation?: boolean;
  // Per-flow customer settings — the flow config is the sole source of truth
  // for these (configured via the flow config page).
  scheduling_timezone?: string;
  timezone_column?: OrchestratorTimezoneColumnConfig;
  user_additional_columns?: { column: RelatedColumn; alias: string }[];
  // history_dimensions and message_completion_interval_days are read DE-level
  // (see DecisionEngineConfig). Retained here so backfilled flow values persist
  // and a future move to per-flow config needs no schema work.
  history_dimensions?: string[];
  message_completion_interval_days?: number;
  /** Allowlist of parent model columns to include in the hightouch_user sync payload. Null/undefined means include all. */
  sync_user_columns?: string[] | null;
  run_schedule?: DecisionEngineFlowRunSchedule;
}

/** Localization only takes effect once a user locale column is configured. */
export function isLocalizationEnabled(
  flowConfig: Pick<
    DecisionEngineFlowConfig,
    "localization_enabled" | "user_locale_field"
  >,
): boolean {
  return Boolean(
    flowConfig.localization_enabled && flowConfig.user_locale_field,
  );
}

type DemoData = {
  userFeatures: {
    name: string;
    values: string[];
  }[];
  actionFeatures: {
    name: string;
    values: string[];
  }[];
  correlationCharts: {
    filter_by: string;
    filter_value: string;
    breakdown_by_dimension: string;
    metadata: {
      chart_description: string;
      conversion_data: {
        breakdown_value: string;
        conversion_rate: number;
        n_total_conversions: number;
        n_total_interactions: number;
        baseline_rate: number;
        rank_score: number;
        credibly_different: boolean;
        direction: EffectDirection | null;
        coverage: number;
      }[];
    };
  }[];
  inspectorData: InspectorDemoData;
};

export type InspectorDemoData = {
  userId: string;
  messages: {
    messageId: string;
    sentAt: string;
    conversionEvents?: {
      label: string;
      type: "email_click" | "purchase";
      date: string;
    }[];
    skipped?: boolean;
  }[];
  primaryLabel: string;
  secondaryLabel: string;
  userFields: Record<string, any>;
  eligibilityEvents: {
    type: "eligible" | "ineligible";
    date: string;
  }[];
}[];

export interface DecisionEngineModelConfig {
  action_model_configuration: ModelConfiguration;
  optuna_config?: OptunaConfig;
  feature_selection_config?: FeatureSelectionConfig;
  /** ISO timestamp. Promotion records from runs that started before it are ignored. */
  demoted_at?: string;
}

export type StratificationGroupOverride = {
  key: string;
  value: number;
};

export type StratificationGroup = {
  feature: string;
  overrides?: StratificationGroupOverride[];
};

export type IntelligentTargetingConfig = {
  enabled: boolean;
  ranking_strategy?: "q_value" | "random";
  filter_strategy?: "hard_filter" | "probabilistic_filter";
  filter_percentage?: number;
  min_filter_probability?: number;
  max_filter_probability?: number;
  temperature?: number;
  stratification_features?: string[];
  stratification_groups?: StratificationGroup[];
  max_sends_per_run?: number;
};

// Mirrors Python ExpectedValueOptimizationConfig in expected_values_config.py.
// `runtime` is intentionally excluded — it's orchestrator-populated, not user-authored.
// A concrete (non-stratified) budget constraint. Currently only `max_average_discount`.
export type ConcreteBudgetConfig = {
  type: "max_average_discount";
  d_max: number;
};

// Optional stratified budget: applies a distinct concrete budget per stratum value (e.g. country code),
export type StratifiedBudgetConfig = {
  type: "stratified";
  strata_feature: string;
  default_budget_config: ConcreteBudgetConfig;
  strata_budget_configs: Record<string, ConcreteBudgetConfig>;
};

export type BudgetConfig = ConcreteBudgetConfig | StratifiedBudgetConfig;

export type ValueOptimizationConfig = {
  mode: "disabled" | "costs_in_outcome" | "ev_unconstrained" | "ev_constrained";
  objective?: "net_value" | "gross_value";
  budget?: BudgetConfig;
  buffer?: number;
  solver_sample_size?: number;
  solver_history_window_days?: number;
  solver_sample_strategy?: "uniform";
};

export type DecisionEngineOutcomeReward = {
  type: "positive" | "negative";
  column?: string;
  priority: number;
  measurementOnly?: boolean;
};

export type DecisionEngineModelOutcomeWeight = {
  reward_value?: number;
  use_dynamic_weights?: boolean;
  reward_mode?: OutcomeRewardMode;
  reward_transform?: OutcomeRewardTransform;
};

export type DecisionEngineOutcomeAttribution = {
  campaign_id_column?: string;
};

export interface DecisionEngineResponse {
  status: string;
}

export const VALID_FEATURE_TYPES = [
  "integer",
  "float",
  "categorical",
  "boolean",
  "datetime",
  "passthrough",
] as const;

export type ValidFeatureType = (typeof VALID_FEATURE_TYPES)[number];

export interface ImputationConfig {
  strategy: "mean" | "median" | "most_frequent" | "constant";
  fill_value?: string | number | boolean | null;
  // add_indicator is the flag for sklearn to add an additional feature for "was_missing"
  add_indicator?: boolean;
}

export type FeatureSchema = {
  name: string;
  type: ValidFeatureType;
  computed?: boolean;
  dependency?: boolean;
  imputation?: ImputationConfig;
}[];

export type ItemFilter = DecisionEngineCondition;

// These are similar to the audience types, but simplified.
// We don't need all the same features as audiences here, so we
// define a subset of the types.
type DecisionEngineAndCondition = {
  type: "and";
  conditions: DecisionEngineCondition[];
};

type DecisionEngineOrCondition = {
  type: "or";
  conditions: DecisionEngineCondition[];
};

type DecisionEngineColumnReference = {
  table: "users" | "items";
  name: string;
};

export type DecisionEngineOperator =
  // Numeric/comparison operators
  | ">"
  | "<"
  | "="
  | "<>"
  | ">="
  | "<="
  | "between"
  | "between-inclusive"
  // String operators
  | "contains"
  | "notcontains"
  | "startswith"
  | "notstartswith"
  | "endswith"
  | "notendswith"
  // Inverse-contains operators
  | "isin"
  | "notisin"
  // Existence operators
  | "exists"
  | "existsandisnotempty"
  | "notexists"
  // Timestamp operators
  | "before"
  | "before-inclusive"
  | "after"
  | "after-inclusive"
  | "within"
  | "notwithin"
  | "anniversary";

export type DecisionEnginePropertyCondition = {
  type: "property";
  property: DecisionEngineColumnReference | string;
  value:
    | string
    | number
    | boolean
    | [string | number | boolean, string | number | boolean]
    | DecisionEngineColumnReference;
  operator: DecisionEngineOperator;
};

export enum DecisionEngineTimeOperator {
  BEFORE = "before",
  BEFORE_INCLUSIVE = "before-inclusive",
  AFTER = "after",
  BETWEEN = "between",
  BETWEEN_INCLUSIVE = "between-inclusive",
}

export enum DecisionEngineTimeDirection {
  FORWARD = "forward",
  BACKWARD = "backward",
}

export type DecisionEngineTimeFilterValue = {
  interval: "day";
  quantity: number;
  direction: DecisionEngineTimeDirection;
};

export type DecisionEngineTimeWindow = {
  operator: DecisionEngineTimeOperator;
  timeType: "relative";
  // For one-sided windows (before/before-inclusive/after), the single bound.
  // For between/between-inclusive, the older bound (the larger lookback); the
  // newer bound lives in `endValue`.
  value: DecisionEngineTimeFilterValue;
  // Only set for between/between-inclusive: the newer bound (smaller lookback).
  endValue?: DecisionEngineTimeFilterValue;
};

export type DecisionEngineTimeCondition = {
  type: "time";
  property: DecisionEngineColumnReference;
  window: DecisionEngineTimeWindow;
};

export type DecisionEngineEventCondition = {
  type: "event";
  event: string;
  operator: DecisionEngineOperator;
  value: number;
  window?: DecisionEngineTimeWindow;
  subconditions?: DecisionEngineCondition;
};

export type DecisionEngineCondition =
  | DecisionEngineAndCondition
  | DecisionEngineOrCondition
  | DecisionEnginePropertyCondition
  | DecisionEngineTimeCondition
  | DecisionEngineEventCondition;

// These are types from the database that we use to define the shape of the
// decision_engine_messages.config JSONB column based on the type of message.
export interface DecisionEngineMessageConfig {
  baseMessageId: string;
}

export interface ScheduleVariant {
  day: Day;
  time: Time;
}

export interface DecisionEngineFlowMessageConfig {
  campaignId: string;
  // Used to track non campaign resources like Klaviyo flows
  resourceId?: string;
  contentResourceId?: string;
  attributionCampaignIds?: string[];
  maxSendsPerUser?: number;
  maxSendsPerUserWindowDays?: number;
  minOtherMessagesInBetween?: number;
  firstEligibleSendDate?: string;
  lastEligibleSendDate?: string;
  userAudienceFilter?: RootCondition;
  eligibleScheduleVariants?: ScheduleVariant[];
  previewSnippetCheck?: Omit<MessagePreviewSnippetCheck, "__typename">;
  previewTemplateRevision?: number;
}

export const DEFAULT_RETENTION_WINDOW_DAYS = 180;
export const MIN_RETENTION_WINDOW_DAYS = 30;
export const MAX_RETENTION_WINDOW_DAYS = 730;

export function getRetentionWindowDays(
  config: Pick<DecisionEngineFlowConfig, "retention_window_days">,
): number {
  return config.retention_window_days ?? DEFAULT_RETENTION_WINDOW_DAYS;
}

export function assertSendLimitWithinRetentionWindow(
  { maxSendsPerUserWindowDays }: Pick<VariableDB, "maxSendsPerUserWindowDays">,
  retentionWindowDays: number | undefined,
  subject: string,
): void {
  if (
    retentionWindowDays != null &&
    maxSendsPerUserWindowDays != null &&
    maxSendsPerUserWindowDays > retentionWindowDays
  ) {
    throw new Error(
      `${subject} has a send limit window of ${maxSendsPerUserWindowDays} days. The agent's retention window allows at most ${retentionWindowDays} days.`,
    );
  }
}

export function assertValidFlowMessageSendLimits(
  {
    maxSendsPerUser,
    maxSendsPerUserWindowDays,
    minOtherMessagesInBetween,
  }: Pick<
    DecisionEngineFlowMessageConfig,
    | "maxSendsPerUser"
    | "maxSendsPerUserWindowDays"
    | "minOtherMessagesInBetween"
  >,
  retentionWindowDays: number | undefined,
): void {
  if (maxSendsPerUserWindowDays && !maxSendsPerUser) {
    throw new Error(
      "max sends per user is required if max sends per user window days is provided",
    );
  }
  if (maxSendsPerUser && minOtherMessagesInBetween) {
    throw new Error(
      "You cannot set both max sends per user and min other messages in between",
    );
  }
  assertValidForeverSendLimit(
    { maxSendsPerUser, maxSendsPerUserWindowDays },
    "The message",
  );
  assertSendLimitWithinRetentionWindow(
    { maxSendsPerUserWindowDays },
    retentionWindowDays,
    "The message",
  );
}

/**
 * Resolves which destination resource id to use when fetching a flow
 * message's content: an explicitly associated content resource wins over the
 * trigger resource (resourceId, then campaignId). Empty strings are treated
 * as unset (e.g. a cleared picker), matching the truthiness checks the UI
 * applies to these fields.
 */
export function getFlowMessageContentResourceId(
  config:
    | Pick<
        DecisionEngineFlowMessageConfig,
        "campaignId" | "resourceId" | "contentResourceId"
      >
    | null
    | undefined,
): string | undefined {
  return (
    config?.contentResourceId ||
    config?.resourceId ||
    config?.campaignId ||
    undefined
  );
}

export interface DecisionEngineOutcomeConfig {
  metadata_column?: string;
  // If specified, we will filter the event table by this column using the campaign IDs from the
  // flow messages.
  campaign_id_column?: string;
  // If specified, the outcome's event timestamp column is interpreted as being in this time zone and
  // converted to UTC at query time. Supports IANA names (DST-aware) or fixed UTC offsets.
  timestamp_timezone?: OutcomeTimezoneConfig;
  // If specified, we will filter the event table by this column against a list of known bot IP addresses.
  ip_address_column?: string;
}

// Metadata column configuration for dynamic send event columns
export interface SendEventMetadataColumn {
  // Column name in the source send event table
  source_column: string;
  // Column name in the destination sends table (optional, defaults to source_column)
  target_column?: string;
}

export interface DecisionEngineSendEventAttribution {
  // If specified, the customer's send_event table has a campaign_id column
  campaign_id_column?: string;
  // If specified, the send event's timestamp column is interpreted as being in this
  // time zone and converted to UTC at query time. Supports IANA names (DST-aware) or
  // fixed UTC offsets. Without it, naive (no-timezone) timestamp columns are
  // interpreted in the warehouse session time zone, which is rarely what the data means.
  timestamp_timezone?: OutcomeTimezoneConfig;
  // Window in minutes for matching sends to recommendations (default 360)
  window_minutes?: number;
  // Additional metadata columns to copy from send events to the sends table
  // These columns can be boolean or integer (converted to boolean)
  metadata_columns?: SendEventMetadataColumn[];
}

export type FlowSendEvent = {
  id: string;
  channel_id: string;
  channel_type: string;
  segmentId: number;
  filter: EventFilter | null;
  attribution: DecisionEngineSendEventAttribution;
};

export enum DecisionEngineRunStatus {
  Pending = "pending",
  Running = "running",
  Success = "success",
  Error = "error",
  Cancelled = "cancelled",
}

export enum DecisionEngineFlowRunStatus {
  Pending = "pending",
  Running = "running",
  Success = "success",
  Error = "error",
  Cancelled = "cancelled",
  Skipped = "skipped",
}

// Typebox types for validation
export const FeatureSchemaTypebox = Type.Array(InputFeatureSchema, {
  minItems: 1,
  uniqueItems: true,
});
export type FeatureSchemaTypebox = Static<typeof FeatureSchemaTypebox>;
export type ConfigInputTypebox = Static<typeof ConfigInputTypebox>;
export enum ScoringMetric {
  // Regression metrics
  NEG_ROOT_MEAN_SQUARED_ERROR = "neg_root_mean_squared_error",
  NEG_MEAN_ABSOLUTE_ERROR = "neg_mean_absolute_error",
  NEG_MEAN_SQUARED_ERROR = "neg_mean_squared_error",
  // Classification metrics
  NEG_LOG_LOSS = "neg_log_loss",
  ROC_AUC = "roc_auc",
}

export enum ClusteringAlgorithm {
  HDBSCAN = "hdbscan",
  KMEANS = "kmeans",
}

export const ClusterConfigTypebox = Type.Union([
  // KMeans configuration - algorithm is required
  Type.Object({
    enabled: Type.Optional(Type.Boolean()),
    algorithm: Type.Literal(ClusteringAlgorithm.KMEANS),
    random_state: Type.Optional(Type.Union([Type.Integer(), Type.Null()])),
    n_clusters: Type.Optional(Type.Integer({ minimum: 1 })),
    init: Type.Optional(
      Type.Union([Type.Literal("k-means++"), Type.Literal("random")]),
    ),
    n_init: Type.Optional(
      Type.Union([Type.Integer({ minimum: 1 }), Type.String()]),
    ),
    max_iter: Type.Optional(Type.Integer({ minimum: 1 })),
    tol: Type.Optional(Type.Number({ minimum: 0 })),
  }),
  // HDBSCAN configuration - algorithm is optional (default)
  Type.Object({
    enabled: Type.Optional(Type.Boolean()),
    algorithm: Type.Optional(Type.Literal(ClusteringAlgorithm.HDBSCAN)),
    random_state: Type.Optional(Type.Union([Type.Integer(), Type.Null()])),
    min_cluster_size: Type.Optional(Type.Integer({ minimum: 1 })),
    min_samples: Type.Optional(
      Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    ),
    cluster_selection_epsilon: Type.Optional(Type.Number({ minimum: 0 })),
    max_cluster_size: Type.Optional(
      Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    ),
    metric: Type.Optional(
      Type.Union([
        Type.Literal("euclidean"),
        Type.Literal("manhattan"),
        Type.Literal("cosine"),
      ]),
    ),
    alpha: Type.Optional(Type.Number({ minimum: 0 })),
    cluster_selection_method: Type.Optional(
      Type.Union([Type.Literal("eom"), Type.Literal("leaf")]),
    ),
  }),
]);
export type ClusterConfigTypebox = Static<typeof ClusterConfigTypebox>;

export const StaticOfferConfigTypebox = Type.Object(
  {
    type: Type.Literal(OfferConfigType.STATIC),
    cost_value: Type.Number({ minimum: 0 }),
    cost_multiplier: Type.Number({ minimum: 0 }),
    cost_cap: Type.Optional(
      Type.Union([Type.Number({ minimum: 0 }), Type.Null()]),
    ),
  },
  { additionalProperties: false },
);

export const ColumnOfferConfigTypebox = Type.Object(
  {
    type: Type.Literal(OfferConfigType.COLUMN),
    value_column: Type.String({ minLength: 1 }),
    cost_column: Type.String({ minLength: 1 }),
    cost_multiplier: Type.Number({ minimum: 0 }),
    cost_value: Type.Number({ minimum: 0 }),
    cost_cap: Type.Optional(
      Type.Union([Type.Number({ minimum: 0 }), Type.Null()]),
    ),
  },
  { additionalProperties: false },
);

export const OfferCostConfigTypebox = Type.Union([
  StaticOfferConfigTypebox,
  ColumnOfferConfigTypebox,
]);

export const OfferConfigTypebox = Type.Object(
  {
    id: Type.String(),
    name: Type.String(),
    description: Type.Union([Type.String(), Type.Null()]),
    external_id: Type.Union([Type.String(), Type.Null()]),
    config: OfferCostConfigTypebox,
    tags: Type.Array(Type.String()),
  },
  { additionalProperties: false },
);

export const DownsampleConfigTypebox = Type.Object(
  {
    zero_reward_downsampling_enabled: Type.Optional(
      Type.Union([Type.Boolean(), Type.Null()]),
    ),
    max_rows_with_zero_reward: Type.Optional(
      Type.Union([Type.Integer(), Type.Null()]),
    ),
    conversion_rate: Type.Optional(
      Type.Union([
        Type.Number({ exclusiveMinimum: 0, exclusiveMaximum: 0.9 }),
        Type.Null(),
      ]),
    ),
    auto_adjust_zero_reward_downsampling: Type.Optional(
      Type.Union([Type.Boolean(), Type.Null()]),
    ),
    max_ipw_weight: Type.Optional(
      Type.Union([
        Type.Number({ exclusiveMinimum: 0, exclusiveMaximum: 50 }),
        Type.Null(),
      ]),
    ),
    temporal_enabled: Type.Optional(Type.Boolean()),
    temporal_decay_rate: Type.Optional(
      Type.Number({ exclusiveMinimum: 0, exclusiveMaximum: 0.3 }),
    ),
    training_matrix_max_rows: Type.Optional(
      Type.Union([Type.Integer({ exclusiveMinimum: 0 }), Type.Null()]),
    ),
  },
  { additionalProperties: false },
);
export type DownsampleConfigTypebox = Static<typeof DownsampleConfigTypebox>;

export const UpliftModelConfigurationTypebox = Type.Object(
  {
    uplift_model_type: Type.Optional(
      Type.Union([
        Type.Literal("s_learner"),
        Type.Literal("dr_learner"),
        Type.Null(),
      ]),
    ),
    uplift_modeling_enabled: Type.Optional(
      Type.Union([Type.Boolean(), Type.Null()]),
    ),
  },
  { additionalProperties: false },
);
export type UpliftModelConfigurationTypebox = Static<
  typeof UpliftModelConfigurationTypebox
>;

// The inner fields reject null because the Python MagnitudeConfig is
// non-nullable with extra="forbid", so an inner null would only surface as a
// failure at train time. Rejecting it at save time keeps the failure visible in
// the admin panel. The outer `magnitude` key still allows null like its
// siblings, since Python maps a null there to the defaults.
export const MagnitudeConfigTypebox = Type.Object(
  {
    objective: Type.Optional(
      Type.Union([
        Type.Literal("regression"),
        Type.Literal("tweedie"),
        Type.Literal("gamma"),
      ]),
    ),
    min_occurrences: Type.Optional(Type.Integer({ minimum: 1 })),
  },
  { additionalProperties: false },
);
export type MagnitudeConfigTypebox = Static<typeof MagnitudeConfigTypebox>;

export const ModelConfigurationTypebox = Type.Object(
  {
    outcome_reward_function_id: Type.Optional(
      Type.Union([Type.String(), Type.Null()]),
    ),
    skip_quality_guards: Type.Optional(Type.Boolean()),
    magnitude: Type.Optional(Type.Union([MagnitudeConfigTypebox, Type.Null()])),
    uplift_model_configuration: Type.Optional(
      Type.Union([UpliftModelConfigurationTypebox, Type.Null()]),
    ),
    primary_scoring_metric: Type.Optional(
      Type.Union([Type.Enum(ScoringMetric), Type.Null()]),
    ),
    n_jobs: Type.Optional(Type.Union([Type.Integer(), Type.Null()])),
    max_concurrent_estimator_fits: Type.Optional(
      Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    ),
    pre_dispatch: Type.Optional(
      Type.Union([Type.Number(), Type.String(), Type.Null()]),
    ),
    downsampling: Type.Optional(
      Type.Union([DownsampleConfigTypebox, Type.Null()]),
    ),
    skip_background_models: Type.Optional(
      Type.Union([Type.Boolean(), Type.Null()]),
    ),
    sklearn_verbosity: Type.Optional(Type.Union([Type.Integer(), Type.Null()])),
    training_window: Type.Optional(
      Type.Union([
        Type.Object({
          start_days_ago: Type.Optional(
            Type.Union([Type.Integer(), Type.Null()]),
          ),
          end_days_ago: Type.Optional(
            Type.Union([Type.Integer(), Type.Null()]),
          ),
          train_eligible_start_date: Type.Optional(
            Type.Union([Type.String(), Type.Null()]),
          ),
          train_eligible_end_date: Type.Optional(
            Type.Union([Type.String(), Type.Null()]),
          ),
        }),
        Type.Null(),
      ]),
    ),
    enable_stagewise_imputation: Type.Optional(
      Type.Union([Type.Boolean(), Type.Null()]),
    ),
    hyperparameter_override: Type.Optional(
      Type.Union([
        Type.Object({
          reward_model: Type.String(),
          params: Type.Object({}),
        }),
        Type.Null(),
      ]),
    ),
    fixed_model_params: Type.Optional(
      Type.Union([Type.Record(Type.String(), Type.Any()), Type.Null()]),
    ),
    training_matrix_filter: Type.Optional(Type.Null()),
  },
  { additionalProperties: false },
);

export type ModelConfigurationTypebox = Static<
  typeof ModelConfigurationTypebox
>;

export const OptunaConfigTypebox = Type.Object({
  enabled: Type.Boolean(),
  model_name: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  n_trials: Type.Optional(Type.Union([Type.Integer(), Type.Null()])),
  search_space: Type.Optional(
    Type.Union([Type.Array(Type.Object({})), Type.Null()]),
  ),
  run_interval_days: Type.Optional(Type.Union([Type.Integer(), Type.Null()])),
  force_run: Type.Optional(Type.Union([Type.Boolean(), Type.Null()])),
  force_trial_params: Type.Optional(
    Type.Union([Type.Array(Type.Object({})), Type.Null()]),
  ),
  n_jobs: Type.Optional(Type.Union([Type.Integer(), Type.Null()])),
  // Rejects null because the Python field is a non-nullable bool with a default.
  reuse_fold_transforms: Type.Optional(Type.Boolean()),
});

export type OptunaConfigTypebox = Static<typeof OptunaConfigTypebox>;

export const FeatureSelectionConfigTypebox = Type.Object({
  enabled: Type.Boolean(),
  eligible_categories: Type.Optional(
    Type.Union([Type.Array(Type.String()), Type.Null()]),
  ),
  must_keep_features: Type.Optional(
    Type.Union([Type.Array(Type.String()), Type.Null()]),
  ),
  default_importance_threshold: Type.Optional(
    Type.Union([Type.Number(), Type.Null()]),
  ),
  category_thresholds: Type.Optional(
    Type.Union([Type.Record(Type.String(), Type.Number()), Type.Null()]),
  ),
  category_min_features: Type.Optional(
    Type.Union([Type.Record(Type.String(), Type.Integer()), Type.Null()]),
  ),
  comparison_score_threshold: Type.Optional(
    Type.Union([Type.Number(), Type.Null()]),
  ),
});

export type FeatureSelectionConfigTypebox = Static<
  typeof FeatureSelectionConfigTypebox
>;

export const ModelMappingTypebox = Type.Object({
  intelligent_targeting: Type.String(),
  scheduling: Type.String(),
  completion: Type.String(),
});

export type ModelMappingTypebox = Static<typeof ModelMappingTypebox>;

export enum ExplorationAlgorithm {
  SQUARECB = "squarecb",
  EPSILON_GREEDY = "epsilon_greedy",
  EPSILON_DECAY = "epsilon_decay",
  RANDOM = "random",
}

export const ExplorationConfig = Type.Object({
  algorithm: Type.Enum(ExplorationAlgorithm),
  epsilon: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
  alpha: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
  beta: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
  // SquareCB gamma; when null, resolves server-side to a bundle matched to enable_scale_normalization
  // (25.0/0.5 scale-norm regime, 2.0/0.7 legacy regime).
  gamma_scale: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
  gamma_exp: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
  new_arm_min_serves: Type.Optional(Type.Union([Type.Integer(), Type.Null()])),
  new_arm_probability_multiplier: Type.Optional(
    Type.Union([Type.Number({ exclusiveMinimum: 0 }), Type.Null()]),
  ),
  // Bayesian Smoothing parameters
  enable_bayesian_smoothing: Type.Optional(Type.Boolean()),
  smoothing_c: Type.Optional(Type.Number({ exclusiveMinimum: 0 })),
  // Level-normalization + sqrt(k) annealing regime: divide SquareCB score gaps by L = median(|per-arm mean score|),
  // anneal gamma on expected conversions (k = N * pooled_base_rate), and select the 25.0/0.5 gamma bundle.
  // Defaults off (behavior-neutral); opt in per-agent.
  enable_scale_normalization: Type.Optional(Type.Boolean()),
  // Derive new_arm_min_serves / smoothing_c from the agent's base rate at serve time
  // (effective serve count = target_k / pooled_base_rate). Defaults off (behavior-neutral); opt in per-agent.
  evidence_scaling: Type.Optional(
    Type.Object({
      enabled: Type.Optional(Type.Boolean()),
      new_arm_target_k: Type.Optional(Type.Number({ exclusiveMinimum: 0 })),
      smoothing_target_k: Type.Optional(Type.Number({ exclusiveMinimum: 0 })),
    }),
  ),
});
export type ExplorationConfig = Static<typeof ExplorationConfig>;

export const BanditConfig = Type.Record(Type.String(), ExplorationConfig);
export type BanditConfig = Static<typeof BanditConfig>;

const StratificationGroupOverrideTypebox = Type.Object(
  {
    key: Type.String(),
    value: Type.Number({ minimum: 0, maximum: 1 }),
  },
  { additionalProperties: false },
);

const StratificationGroupTypebox = Type.Object(
  {
    feature: Type.String(),
    overrides: Type.Optional(Type.Array(StratificationGroupOverrideTypebox)),
  },
  { additionalProperties: false },
);

export const IntelligentTargetingConfigTypebox = Type.Object(
  {
    enabled: Type.Optional(Type.Boolean()),
    ranking_strategy: Type.Optional(
      Type.Union([Type.Literal("q_value"), Type.Literal("random")]),
    ),
    filter_strategy: Type.Optional(
      Type.Union([
        Type.Literal("hard_filter"),
        Type.Literal("probabilistic_filter"),
      ]),
    ),
    filter_percentage: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
    min_filter_probability: Type.Optional(
      Type.Number({ minimum: 0, maximum: 1 }),
    ),
    max_filter_probability: Type.Optional(
      Type.Number({ minimum: 0, maximum: 1 }),
    ),
    temperature: Type.Optional(Type.Number({ minimum: 0, maximum: 10 })),
    stratification_features: Type.Optional(Type.Array(Type.String())),
    stratification_groups: Type.Optional(
      Type.Array(StratificationGroupTypebox),
    ),
    max_sends_per_run: Type.Optional(Type.Number()),
  },
  { additionalProperties: false },
);
export type IntelligentTargetingConfigTypebox = Static<
  typeof IntelligentTargetingConfigTypebox
>;

export const CollectionInputTypebox = Type.Object({
  schema: FeatureSchemaTypebox,
});

export const ConfigInputTypebox = Type.Object({
  name: Type.String(),
  output_schema: Type.Optional(Type.String()),
  message_completion_interval_days: Type.Optional(Type.Integer()),
  touch_cached_interactions_older_than_days: Type.Optional(Type.Integer()),
  slack_channel: Type.Optional(Type.String()),
  scheduling_timezone: Type.Optional(Type.String()),
  ignore_s3_state: Type.Optional(Type.Boolean()),
});

/**
 * Internal destination type that routes agent sends through the workspace's
 * native delivery channels. One row per workspace, created on demand.
 */
export const NATIVE_DELIVERY_DESTINATION_TYPE = "hightouch-native-delivery";

export const DECISION_ENGINE_SUPPORTED_DESTINATIONS: Record<
  DecisionEngineChannelType,
  string[] | null
> = {
  email: [
    "iterable",
    "sfmc",
    "braze",
    "klaviyo",
    "eloqua",
    "adobe-experience-platform",
    "cordial",
    "adobe-campaign-classic",
    "mongodb",
    "marketo",
    "moengage",
    NATIVE_DELIVERY_DESTINATION_TYPE,
  ],
  sms: [
    "iterable",
    "sfmc",
    "braze",
    "klaviyo",
    "attentive",
    "adobe-experience-platform",
    "onesignal",
    "adobe-campaign-classic",
    "mongodb",
    "marketo",
    "moengage",
    NATIVE_DELIVERY_DESTINATION_TYPE,
  ],
  push: [
    "iterable",
    "sfmc",
    "braze",
    "klaviyo",
    "adobe-experience-platform",
    "onesignal",
    "mongodb",
    "airship",
    "moengage",
    NATIVE_DELIVERY_DESTINATION_TYPE,
  ],
  inapp: ["iterable", "sfmc", "braze", "mongodb", "onesignal"],
  raw: null, // Support all destinations
  surface: ["hightouch-personalization-api", "mongodb"],
  journey: null, // Inherits the engine's destination
};

export interface DecisionEngineCollectionConfig {
  name: string;
  collection_id: string;
  schema: FeatureSchema;
  file_path: string;
  item_filter: ItemFilter;
}

export interface DecisionEngineCollectionInput {
  name: string;
  collection_id: string;
  feature_schema: FeatureSchema;
  file_path: string;
  item_filter: ItemFilter;
}

export interface UserCollectionItem {
  item_id: string;
  score: number;
  rank: number;
}

export interface UserCollectionItems {
  user_id: string;
  collection_id: string;
  items: UserCollectionItem[];
}

export interface SharedInteractionsInput {
  /** schedule_until is the active override period's end, so a period run never schedules past it. */
  timing: RecommenderTiming & { schedule_until?: Date };
  /** Base timing plus each override period's timing. Earlier runs may have scheduled under any of them. */
  recent_override_timings?: RecommenderTiming[];
  channels: DecisionEngineChannel[];
  disabled_channels: DecisionEngineChannel[];
  collection_feature_schemas: Pick<
    DecisionEngineCollectionInput,
    "collection_id" | "feature_schema"
  >[];
  users: {
    scheduling_timezone: string;
    timezone_column?: TimezoneColumnConfig;
    feature_schema: FeatureSchema;
  };
  previous_run_evaluation_metrics: Array<PreviousActionModelEvaluationMetrics>;
  experiments: {
    name: string;
    audience_percent: number;
  }[];
  tag_configs: Array<TagConfig>;
  eligibility_tag_keys?: string[];
  // Localization settings
  localization_enabled?: boolean;
  default_locale?: string;
  user_locale_field?: string;
  locale_fallback_behavior?: "default_locale" | "skip" | "throw";
  /** List of supported locale codes from the agent's locales config */
  supported_locales?: string[];
  flow_type: DecisionEngineFlowType;
}

export interface RemoveFutureDisabledMessagesApiInput
  extends SharedInteractionsInput {
  cached_interactions_manifest_file_path: string;
  completed_interactions_output_manifest_file_path: string;
  /** NDJSON of the interaction ids removed for breaking period rules, which the caller deletes from the warehouse. */
  period_rule_removed_ids_file_path: string;
  /**
   * Anchors the today-1 scan window, is the cutoff for period rule removals, and is the fallback
   * cutoff for disabled messages with no disabled_at. Serializes to a UTC ISO timestamp for pydantic.
   */
  now: Date;
}

export interface ConvertInteractionsApiInput extends SharedInteractionsInput {
  input_interactions_directory: string;
  output_interactions_directory: string;
  completed_interactions_output_manifest_file_path: string;
}

export interface AttributionConfig {
  attribution_window: number;
  attribution_type?: AttributionType;
  decay_strategy?: string;
  half_life_days?: number;
}

export interface SharedApiInput extends SharedInteractionsInput {
  start_at?: number;
  completed_interactions_input_manifest_file_path: string;
  rewards_file_path: string;
  sends_file_path: string;
  filter_unsent_interactions?: boolean;
  decision_engine_name: string;
  decision_engine_id: string;
  workspace_id: string;
  flow_id: string;
  attribution_config: AttributionConfig;
}

export interface CompactInteractionsApiInput extends SharedApiInput {
  output_manifest_file_path: string;
  output_interactions_directory: string;
  compact_num_rows: number;
  time_limit_seconds: number;
}

export interface TimezoneColumnConfig {
  name: string;
  format: "iana" | "offset_string";
}

export interface ModelArtifacts {
  variant_name?: string;
  action_model_path: string | null;
  action_model_metadata_path: string | null;
  action_model_configuration: ModelConfiguration;
  /** From the promotion record: the loaded artifact is a trained model, not the uniform fallback. */
  serves_trained_model?: boolean;
  test_matrix_path?: string;
  val_matrix_path?: string;
}

export interface ModelMapping {
  intelligent_targeting: string;
  scheduling: string;
  completion: string;
}

export interface SplitVariantConfig {
  variant_name?: string;
  audience_percent?: number;
  arm_counts_path: string | null;
  intelligent_targeting: string;
  scheduling: string;
  completion: string;
}

export interface SplitConfig {
  split_name?: string;
  variants: SplitVariantConfig[];
}

export interface SharedPolicyApiInput extends SharedApiInput {
  bandits?: BanditConfig;
  flow_start_date?: string;
  incrementality_experiment_start_date?: string;
  message_completion_interval_days?: number;
  models: Record<string, ModelArtifacts>;
  split: SplitConfig;

  outcomes: OutcomeConfigBase[];
  outcome_reward_config: { outcomes: OutcomeConfig[] };
  num_generate_interactions_subprocesses?: number;
  max_df_size_gb?: number;
  max_users_per_subprocess?: number;
  total_users_ht?: number;

  // The suffixed ID for this decision engine run
  decision_engine_run_id: string;
  // Seeds interaction ids and the budget sample in place of decision_engine_run_id; set on dry runs started from another run
  replay_seed?: string;

  intelligent_targeting?: IntelligentTargetingConfig;
  uniform_predictive_modeling?: boolean;
  allow_multishot?: boolean;
  enable_exhaustive_schedule_selection?: boolean;
  value_optimization?: ValueOptimizationConfig;
  use_uniform_fallback?: boolean;
}

export interface GenerateInteractionsApiInput extends SharedPolicyApiInput {
  // a manifest file for the completed interactions
  completed_interactions_output_manifest_file_path: string;
  // an ndjson file with just the new ready to send interactions of this run.
  completed_interactions_incremental_ndjson_output_file_path: string;
  policy_data_output_directory: string;
  intelligent_targeting_output_directory: string;
  collections: DecisionEngineCollectionInput[];
  users: {
    scheduling_timezone: string;
    timezone_column?: TimezoneColumnConfig;
    feature_schema: FeatureSchema;
    file_path: string;
    schema_mapping: Record<string, string>;
  };
  user_collection_items_file_path?: string;
  progress_file_path?: string;
}

export interface SavePlotsApiInput extends SharedPolicyApiInput {
  visualizations_output_directory?: string;
  policy_importance_output_path?: string;
  cumulative_conversion_chart_path?: string;
  impact_of_filtering_chart_path?: string;
  outcome_id_to_channel_id_mapping: Record<string, string[]>;
  calibration_sample_rate?: number;
  policy_data_directory: string;
  intelligent_targeting_data_directory: string;
  dry_run?: boolean;
  analytics_facet_feature?: string;
  previous_run_scheduling_metrics: Array<PreviousRunSchedulingMetrics>;
  previous_run_budget_metrics: Array<PreviousRunBudgetMetrics>;
}

export interface GenerateBreakdownsApiInput extends SharedApiInput {
  breakdown_config: BreakdownConfig[];
  filter_unsent_interactions?: boolean;
  // Local directory the recommender reads/writes per-day decoded interaction parquet to. The
  // orchestrator downloads this flow's cached files here before the run and uploads new ones after,
  // so unchanged days skip the (expensive) decode. Omitted/undefined disables the cache.
  breakdown_decode_cache_dir?: string;
}

export interface AggregationsApiInput extends SharedApiInput {
  input_message_breakdown_parquet_file_path: string;
  output_message_breakdown_parquet_file_path: string;
  input_message_counts_parquet_file_path: string;
  output_message_counts_parquet_file_path: string;
  input_skipped_message_counts_parquet_file_path: string;
  output_skipped_message_counts_parquet_file_path: string;
  input_unique_users_skipped_message_counts_parquet_file_path: string;
  output_unique_users_skipped_message_counts_parquet_file_path: string;
  // Hash-partitioned: directory of part-{NNN}.parquet, one per partition.
  input_user_send_counts_parquet_dir: string;
  output_user_send_counts_parquet_dir: string;
  num_user_partitions: number;
  flow_aggregation_output_file_path: string;
  message_aggregation_output_file_path: string;
  input_cached_data_through_date?: string | null;
  cached_data_timezone?: string | null;
  flow_start_date?: string | null;
  cached_flow_start_date?: string | null;
  // Omitted when retention is not enforced; the Python default means no floor.
  retention_floor_date?: string;
}

export interface AggregationsCachedMetadata {
  cachedDataThroughDate?: string | null;
  timezone?: string | null;
  flowStartDate?: string | null;
  version?: number;
  // Number of hash partitions the cached user_send_counts was written with.
  // If the current code's partition count differs, the cache is invalidated —
  // a mismatched N silently undercounts because the loader only reads 0..N-1.
  userSendCountsNumPartitions?: number;
}

export type BreakdownGenerationConfigInternal = {
  // Deprecated: use outcome_ids instead.
  outcome_id: string;
  outcome_ids?: string[];
  outcome_channel_ids: string[] | null;
  number_top_charts_to_cache?: number;
  credible_interval?: number;
  bucketed_group_by_num_bins?: number;
  ideal_breakdown_points?: number;
  variance_discount_power?: number;
  max_charts_per_breakdown?: number;
  history_days?: number;
  skip_correlation_charts?: boolean;
  enable_heatmap_charts?: boolean;
};

export type BreakdownConfig = {
  breakdown_config: RecommenderBreakdownConfig;
  output_global_user_breakdown_path: string;
  output_global_action_breakdown_path: string;
  output_user_correlation_breakdown_path: string;
  output_action_correlation_breakdown_path: string;
  output_action_to_action_correlation_breakdown_path: string;
  output_heatmap_breakdown_path?: string;
};

export type RecommenderBreakdownConfig = Omit<
  BreakdownGenerationConfigInternal,
  "outcome_ids"
> & {
  user_features: string[];
  action_features: string[];
};

export type HyperparameterBestTrial = {
  reward_model: string;
  params: object;
  dropped_features?: string[];
};

export interface ModelInputsApiInput extends SharedApiInput {
  model_name: string;
  model_id: string;
  split_name: string;
  split_variant: string;
  action_model_configuration?: ModelConfiguration;
  outcome_reward_config: {
    outcomes: OutcomeConfig[];
  };
}

export interface TrainModelsApiInput extends ModelInputsApiInput {
  action_model_output_path: string;
  arm_counts_output_path: string;
  action_model_metadata_output_path: string;
  training_matrix_output_path: string;
  test_matrix_output_path: string;
  validation_matrix_output_path: string;
  intelligent_targeting_data_directory?: string;
  action_model_hyperparam_best_trial?: HyperparameterBestTrial;
  action_model_champion_params?: HyperparameterBestTrial;
  /**
   * Ask the recommender to estimate the marginal per-arm selection
   * probabilities and return them in its metadata. Only journey flows need it:
   * they select an arm in the warehouse and so cannot draw per user.
   */
  publish_population_split?: boolean;
}

// feature categories for feature selection.
export type FeatureCategory =
  | "user"
  | "action"
  | "user_history"
  | "action_history"
  | "engagement";

// default value overrides for feature selection config fields
export interface FeatureSelectionConfig {
  enabled?: boolean;
  eligible_categories?: FeatureCategory[];
  must_keep_features?: string[];
  default_importance_threshold?: number;
  category_thresholds?: Partial<Record<FeatureCategory, number>>;
  category_min_features?: Partial<Record<FeatureCategory, number>>;
  comparison_score_threshold?: number;
}

export interface SearchHyperparamsApiInput extends TrainModelsApiInput {
  optuna_config: OptunaConfig;
  feature_selection_config: FeatureSelectionConfig;
  /**
   * Wall-clock budget for this model's search, so it stops with a result the phase can persist
   * rather than being killed mid-search. Omitted falls back to python's whole-pod ceiling.
   */
  search_budget_seconds?: number;
}

export interface ResolvedSearchHyperparametersConfig
  extends SearchHyperparamsApiInput {
  // All optional fields from SearchHyperparamsApiInput are guaranteed to have values
  // after Pydantic validation and default Field construction on the python side.
  // Here we override optional fields and mark them as required.
  action_model_configuration: ModelConfiguration;
  action_model_hyperparam_best_trial: HyperparameterBestTrial;
  optuna_config: OptunaConfig;
  feature_selection_config: Required<FeatureSelectionConfig>;
}

export interface ResolveSearchHyperparametersConfigResponse {
  status: string;
  message?: string | null;
  metadata: ResolvedSearchHyperparametersConfig;
}

export interface PreviousActionModelEvaluationMetrics {
  model_name: string;
  score_type: "base_case_test" | "base_case_train" | "train" | "test";
  timestamp: string;
  neg_mean_squared_error?: number;
  neg_mean_absolute_error?: number;
  neg_root_mean_squared_error?: number;
  r2?: number;

  neg_log_loss?: number;
  roc_auc?: number;
}

export interface Scores {
  neg_mean_squared_error?: number;
  neg_mean_absolute_error?: number;
  neg_root_mean_squared_error?: number;
  neg_log_loss?: number;
  roc_auc?: number;
}

export interface TrainingResult {
  accepted: boolean;
  scores: Scores;
  train_scores?: Scores;
  reward_model: string;
  params: object;
  dropped_features?: string[];
}

export interface SchedulingMetrics {
  num_interactions_considered_by_scheduler: number;
  num_interactions_scheduled: number;
  num_interactions_skipped_for_processing_window: number;
  num_interactions_skipped_for_completion_window_blackout: number;
  num_interactions_skipped: number;
  num_interactions_with_no_eligible_arms: number;
  num_interactions_filtered_by_user_frequency_limit: number;
  num_interactions_filtered_by_quiet_period: number;
  num_interactions_filtered_by_eligible_days: number;
  num_interactions_filtered_by_blackout_days: number;
}

export interface GenerateInteractionsMetrics {
  completed_interactions: number;
  scheduled_interactions: number;
  num_filtered_interactions: number;
  num_kept_interactions: number;
  num_recency_filtered_interactions: number;
  num_eligibility_filtered_interactions: number;
  num_intelligent_targeting_filtered_interactions: number;
  num_budget_suppressed_interactions: number;
  total_interaction_count: number;
  num_starting_users: number;
  num_users_post_recency_filter: number;
  num_users_with_any_eligible_message: number;
  num_scheduled_users: number;
  num_completed_users: number;
  num_kept_users: number;
}

// TODO(DE-3318): drop the `num_users_*` legacy fields once all T14 runs use the new names.
export type SchedulingMetricsFromMetadata = Partial<SchedulingMetrics> & {
  num_users_considered?: number;
  num_users_with_no_eligible_arms?: number;
  num_users_filtered_by_user_frequency_limit?: number;
  num_users_filtered_by_quiet_period?: number;
  num_users_filtered_by_eligible_days?: number;
  num_users_filtered_by_blackout_days?: number;
};

export interface BudgetMetricsFromMetadata {
  g_realized_sum?: number;
  ec_realized_sum?: number;
  er_realized_sum?: number;
  num_paid_arm_assignments?: number;
  strata?: Record<string, BudgetMetricsFromMetadata>;
}

export type GenerateInteractionsMetricsFromMetadata =
  Partial<GenerateInteractionsMetrics> & {
    scheduling_metrics?: SchedulingMetricsFromMetadata;
    budget_metrics?: BudgetMetricsFromMetadata;
  };

// Per-run counters consumed by the save-plots scheduling-metrics charts.
// Narrowed to just the fields the plots need; mirrors the Python-side class in config.py.
export interface PreviousRunSchedulingMetrics {
  decision_engine_run_id: string;
  timestamp: string;

  num_starting_users: number;
  num_users_post_recency_filter: number;
  num_users_with_any_eligible_message: number;
  num_scheduled_users: number;
  num_completed_users: number;
  num_kept_users: number;

  num_recency_filtered_interactions: number;
  num_eligibility_filtered_interactions: number;
  scheduled_interactions: number;
  completed_interactions: number;
  num_kept_interactions: number;
  num_intelligent_targeting_filtered_interactions: number;
  total_interaction_count: number;

  num_interactions_skipped_for_processing_window: number;
  num_interactions_skipped_for_completion_window_blackout: number;
  num_interactions_with_no_eligible_arms: number;
}

// mirrors the Python PreviousRunBudgetSnapshot pydantic model
export interface PreviousRunBudgetSnapshot {
  d_max: number;
  lambda_star: number;
  effective_cap: number;
  g_realized_sample: number;
  n_sampled: number;
  g_realized_sum: number;
  ec_realized_sum: number;
  er_realized_sum: number;
  num_paid_arm_assignments: number;
}

// per-stratum slice of PreviousRunBudgetMetrics.
// solver fields come from the matching ConstrainedRunContext variant
// realized sums come from budget_metrics.strata[stratum_value]
export interface PreviousRunStratumBudgetMetrics
  extends PreviousRunBudgetSnapshot {
  stratum_value: string;
}

// per-run budget-aware policy snapshot consumed by the budget-health plots.
// mirrors the Python PreviousRunBudgetMetrics pydantic model
export interface PreviousRunBudgetMetrics extends PreviousRunBudgetSnapshot {
  decision_engine_run_id: string;
  timestamp: string;
  mode: string;
  budget_type: string;
  buffer: number;
  solver_version: string;
  solved_at: string;
  num_budget_suppressed_interactions: number;
  strata: PreviousRunStratumBudgetMetrics[];
}

// Parallelism knobs a generate-interactions-batch delivery actually ran with;
// a redelivery reduces from these so cuts compound.
export interface GenerateInteractionsBatchProfileParams {
  num_generate_interactions_subprocesses?: number | null;
  max_users_per_subprocess?: number | null;
  batch_index: number;
  attempt_number: number;
  user_count: number;
}

// Resource profile written every ~30s to the run row's `metadata->'profile'`;
// a redelivery rotates it to `metadata->'profile_previous'`. Job-specific
// parameters nest under `params`.
export interface FlowRunResourceProfile {
  // Peak/avg container working-set memory and the cgroup memory limit.
  peak_rss_gb: number;
  avg_rss_gb: number;
  mem_limit_gb: number;
  job_type: string;
  updated_at: string;
  params?: {
    "generate-interactions-batch"?: GenerateInteractionsBatchProfileParams;
  } | null;
}

// Metadata for the generate-interactions step. Only the fields we consume
// (model_metadata, metrics) are typed here; the full payload is larger.
export interface GenerateInteractionsMetadata {
  model_metadata: Record<
    string, // model name
    {
      training_results: TrainingResult[];
    }
  >;
  metrics?: GenerateInteractionsMetricsFromMetadata;
}

export interface MetadataResponse {
  "generate-interactions": {
    metadata: GenerateInteractionsMetadata;
  };
}

// Generic wrapper for lists of file paths in object storage
// Used for policy data and intelligent targeting data
export interface FileListManifest {
  files: string[];
}

// Mirrors the PlotInfo type in decision_engine/decision_engine/plotting/plot_info.py
export type PlotInfo = {
  plot_type: string;
  plot_name: string;
  plot_path: string;
  data_path?: string;
  experiment?: string;
  outcome?: string;
  dimension?: string;
  normalization?: string;
  time_frame?: string;
  split_variant?: string;
  facet_feature?: string;
  facet_value?: string;
};

export interface ClientPlotInfo extends PlotInfo {
  bucket: string;
  key: string;
  data_key?: string;
}

export type ClientPlotConfig = Pick<
  SavePlotsApiInput,
  | "timing"
  | "channels"
  | "bandits"
  | "models"
  | "split"
  | "intelligent_targeting"
  | "outcomes"
>;

export interface TagConfig {
  tag_key: string;
  tag_value: string;
  timing?: BaseRecommenderTiming;
}

// Feature Matrix Configuration Types
export enum FMRelationshipConfigAggType {
  COUNT = "count",
  COUNT_DISTINCT = "count_distinct",
  SUM = "sum",
  AVERAGE = "average",
  MIN = "min",
  MAX = "max",
  MODE = "mode",
  FIRST = "first",
  LAST = "last",
}

// Kept in a dependency-free module so journey config types can import it without zapatos.
export { FM_COLUMN_TYPE_UNIX_TIMESTAMP, type FMColumnType };

/**
 * Represents a column in the feature matrix segment configuration.
 *
 * - name: The column name
 * - modelId: Disambiguates columns with the same name from different models
 *   (e.g., when merged columns from multiple related models share a column name).
 *   This is the PK of the model_columns table.
 * - aggregations: Array of aggregation types to apply. Empty array = no aggregation (pass-through).
 *   For columns from relationships where requiresAggregation=true, this must be non-empty.
 * - filters: array of conditions to apply for this column. Each one will be cross with timeWindowDays
 *   to create independent features.
 * - includeUnfiltered: also emit the unfiltered variants alongside the filtered ones.
 * - timeWindowDays: Optional time windows for time-based aggregations. (overrides relation level timeWindowDays)
 */
export interface FMRelationshipColumn {
  name: string;
  modelId: string;
  aggregations: FMRelationshipConfigAggType[];
  /** Optional filter conditions to apply in CASE WHEN for this column's aggregations. Each filter produces its own set of aggregated columns. */
  filters?: (
    | PropertyCondition
    | AndCondition<PropertyCondition>
    | OrCondition<PropertyCondition>
  )[];
  /** Redundant when `filters` is empty, since that already yields the unfiltered variants. */
  includeUnfiltered?: boolean;
  timeWindowDays?: number[];
  dependency?: boolean;
  castTo?: FMColumnType;
  imputation?: ImputationConfig;
  /** When true, this column is included in insights breakdowns. undefined/false = excluded (default). */
  insightsEnabled?: boolean;
  /** Unprefixed variant names to exclude (e.g., ["amount_sum_7d", "amount_avg_a3f2c1d4_30d"]). Stale entries harmlessly ignored. */
  insightsExcludedVariants?: string[];
  /**
   * When true, every `user_features` key this column generates is nulled for users
   * who appear in the feature matrix's consent-revocations model. Requires
   * `consentPurge` on the parent feature matrix (see `FeatureMatrixConfig`).
   */
  sanitizeOnConsentRevocation?: boolean;
}

/**
 * Consent-revocations model registered on a feature matrix. Presence of this config
 * is what enables the consent purge.
 */
export interface ConsentPurgeConfig {
  /**
   * `segments.id` of the revocations model, stringified because it crosses GraphQL
   * JSON boundaries where bigint overflows Int. Must live on the engine's source.
   * Compare with `String(segment.id)`, never `===` against a number.
   */
  revocationsModelId: string;
  columns: {
    /** Column holding the user primary key, matching the feature matrix's parent model. */
    primaryKey: string;
    /** Timestamp column holding when the user revoked consent. */
    revokedAt: string;
  };
  /** Absent reads as `DEFAULT_CONSENT_PURGE_MODE`. */
  mode?: ConsentPurgeMode;
}

/**
 * A column that was flagged `sanitizeOnConsentRevocation` but was later deleted. Its
 * `user_features` keys persist in cached history, so the purge job keeps nulling them
 * until the column is flagged again.
 */
export interface FeatureMatrixTombstone {
  modelName: string;
  sourceColumn: string;
  /** Prefixed `user_features` keys, e.g. `user_feature_orders_12_amount_sum_7d`. */
  variantKeys: string[];
  /**
   * ISO 8601, when the column was unflagged. Set by whoever writes the config rather
   * than by the database, so treat it as display context and not as an audit record.
   */
  createdAt: string;
}

/**
 * `decision_engine_feature_matrices.config` — not user-writable through Hasura.
 * Each section is written by its own resolver, which merges into the current row
 * under a row lock so the other sections survive concurrent and stale saves.
 */
export interface FeatureMatrixConfig {
  consentPurge?: ConsentPurgeConfig;
  tombstones?: FeatureMatrixTombstone[];
}

/** Breakdowns get noisy past this many insights columns, so saving above it is blocked. */
export const MAX_INSIGHTS_COLUMNS = 50;

/**
 * Configuration for a feature matrix segment.
 *
 * - relationshipId: Which relationship path from parent model to use (undefined for parent model segments).
 *   This determines join columns and whether aggregation is required.
 * - columns: Array of columns to include, each with explicit aggregation types.
 * - timeWindowDays: Optional time windows for time-based aggregations.
 * - timestampColumn: Optional override for time-based filtering (falls back to event_models table).
 * - filters: Optional filters to apply to the segment.
 *
 * Note: COUNT is always added automatically for each time window when any aggregations are present.
 * Output column names are auto-generated as: {column_name}_{aggregation}_{time_window}d
 * e.g., "amount_sum_7d", "amount_avg_30d", "count_7d"
 */
export interface FMRelationshipConfig {
  columns: FMRelationshipColumn[];
  timeWindowDays?: number[];
  timestampColumn?: string; // Optional override for time-based filtering (uses event_models.timestamp_column as fallback)
  isLegacyFeatureMatrixModel?: boolean;
}

// Feature Matrix - Downstream model with columns and aggregation metadata
export interface SegmentWithColumns {
  modelId: string;
  modelName: string;
  requiresAggregation: boolean; // True if any path from parent contains 1:many relationship
  isEventModel: boolean; // Determines if time window UI should be shown
  timestampColumn?: string; // For event models, the column to use for time windows
  columns: FilterableColumn[]; // Includes columns merged from downstream (preserves original model_id)
}

// Feature Matrix - Parent model entry (never needs aggregation by definition)
export interface ParentModelWithColumns {
  modelId: string;
  modelName: string;
  columns: FilterableColumn[]; // Parent columns
}

// Feature Matrix - Relationship-indexed entry with target model columns
// Key insight: Same model can be reached via multiple relationships with different aggregation needs
export interface RelationshipWithColumns {
  relationshipId: string; // PRIMARY KEY: index by relationship, not model
  relationshipName: string | null;
  targetModelId: string; // The model this relationship points to
  targetModelName: string;
  requiresAggregation: boolean; // Based on relationship cardinality: true if 'many'
  isEventModel: boolean;
  timestampColumn?: string;
  columns: FilterableColumn[]; // Columns from target model (includes merged downstream columns)
  // Tracks where this relationship's columns are also available
  // "parent" = merged into parent model; relationship ID = merged into that relationship
  // Empty array = columns only available in this relationship
  mergedIntoRelationshipIds: string[];
}

// Feature Matrix - Complete result structure with relationship-based indexing
export interface FeatureMatrixColumnsResult {
  parentModel: ParentModelWithColumns; // The root model (no relationship, no aggregation needed)
  relationships: Map<string, RelationshipWithColumns>; // Map<relationshipId, columns>
}

// Feature Matrix - GraphQL-friendly version with array instead of Map
export interface FeatureMatrixColumnsGraphQLResult {
  parentModel: ParentModelWithColumns;
  relationships: RelationshipWithColumns[]; // Array for GraphQL serialization
}
